import { StreamSettingResponse } from "@switcherstudio/api-core-client";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { youtube } from "api/youtube/youtube-client";
import { facebook } from "api/facebook/facebook-client";
import { client } from "api/client-core";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { getChannelName } from "helpers/streamSettings";
import rollbar from "helpers/rollbar";

/**
 * Identifiers needed to cancel a destination's event on the platform itself.
 * Captured before the destination is deleted - the stream setting that carries
 * them is gone by the time the cancellation runs.
 */
interface PlatformEventRefs {
    discriminator?: string;
    youTubeBroadcastId?: string;
    youTubeStreamId?: string;
    facebookVideoId?: string;
    channelName?: string;
}

const getPlatformEventRefs = (
    destination: StreamSettingResponse
): PlatformEventRefs => ({
    discriminator: destination?.Details?.Discriminator,
    youTubeBroadcastId: destination?.Details?.YouTubeBroadcastId,
    youTubeStreamId: destination?.Details?.YouTubeStreamId,
    facebookVideoId: destination?.Details?.FacebookVideoId,
    // The display name, not Details.ChannelName - for YouTube/Facebook/Twitch the
    // latter holds the event title rather than the channel the user recognises.
    channelName: getChannelName(destination)
});

/**
 * Cancels the live event a destination created on its platform. Twitch and plain
 * RTMP have no per-event object to cancel - dropping the Cloudflare output is the
 * whole job there.
 */
const cancelPlatformEvent = async (refs: PlatformEventRefs): Promise<void> => {
    switch (refs.discriminator) {
        case "YouTubeSwitcherStreamSetting": {
            if (!refs.youTubeBroadcastId) return;
            await youtube.liveBroadcasts.delete({ id: refs.youTubeBroadcastId });
            // The bound stream is a separate resource and is not removed with the
            // broadcast, so it would otherwise linger on the user's channel.
            if (refs.youTubeStreamId) {
                await youtube.liveStreams.delete({ id: refs.youTubeStreamId });
            }
            break;
        }
        case "FaceBookSwitcherStreamSetting": {
            if (!refs.facebookVideoId) return;
            await facebook.stream.delete(refs.facebookVideoId);
            break;
        }
        default:
            break;
    }
};

/**
 * Removes a destination from a scheduled livestream (sc-56803).
 *
 * Runs our own teardown first, then cancels the event on the platform. That order is
 * the inverse of how a destination is connected, and it is the one that fails safely:
 * if the platform call fails the destination is still cleanly gone from the livestream
 * and the only residue is an orphaned event that was never streamed to. The reverse
 * order would leave a livestream restreaming to an endpoint the user believes is
 * removed, which nothing downstream can recover from.
 */
export const useRemoveDestination = (refetchBroadcast?: () => void) => {
    const dispatch = useDispatch();
    const [removingIds, setRemovingIds] = useState<Record<string, boolean>>({});

    const removeDestination = useCallback(
        async (broadcastId: string, destination: StreamSettingResponse) => {
            const streamSettingId = destination?.Details?.SwitcherStreamSettingId;
            if (!broadcastId || !streamSettingId) return;

            const refs = getPlatformEventRefs(destination);

            setRemovingIds((prev) => ({ ...prev, [streamSettingId]: true }));

            try {
                await client.broadcasts_RemoveBroadcastDestination(
                    broadcastId,
                    streamSettingId
                );
            } catch (e) {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: "livestreams-page:remove-destination-error"
                    })
                );
                rollbar.error("Failed to remove livestream destination", e, {
                    broadcastId,
                    streamSettingId,
                    discriminator: refs.discriminator
                });
                setRemovingIds((prev) => ({
                    ...prev,
                    [streamSettingId]: false
                }));
                return;
            }

            // Past this point the destination is gone from the livestream, so a
            // failure below is reported but never rolled back.
            try {
                await cancelPlatformEvent(refs);

                dispatch(
                    addNotification({
                        type: NotificationType.Success,
                        message: "livestreams-page:remove-destination-success"
                    })
                );
            } catch (e) {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message:
                            "livestreams-page:remove-destination-platform-error",
                        messageOptions: {
                            channelName: refs.channelName ?? ""
                        }
                    })
                );
                rollbar.error(
                    "Removed livestream destination but failed to cancel its platform event",
                    e,
                    {
                        broadcastId,
                        streamSettingId,
                        discriminator: refs.discriminator,
                        youTubeBroadcastId: refs.youTubeBroadcastId,
                        facebookVideoId: refs.facebookVideoId
                    }
                );
            } finally {
                setRemovingIds((prev) => ({
                    ...prev,
                    [streamSettingId]: false
                }));
                refetchBroadcast?.();
            }
        },
        [dispatch, refetchBroadcast]
    );

    return { removeDestination, removingIds };
};
